import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { requireBackoffice } from '../auth/guard.ts'
import { numericIdParam, parseOneOf, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

// The girone's card: who is entered, its calendar and its standings.
// Loaded with useQuery and not a loader, `pitchDetailRoute`'s reason: the 404 stays in the page.

/** The card's sections. In the URL, so a colleague can be sent straight to the calendario. */
export const LEAGUE_GROUP_SECTIONS = ['entries', 'calendar', 'standings'] as const

export type LeagueGroupSection = (typeof LEAGUE_GROUP_SECTIONS)[number]

export interface LeagueGroupDetailSearch {
  readonly section: LeagueGroupSection
  /** The giornata the calendar shows. In the URL so the way back from a fixture lands on it;
   *  absent, the calendar opens on the one being played. */
  readonly matchday?: number
}

export const DEFAULT_LEAGUE_GROUP_DETAIL_SEARCH: LeagueGroupDetailSearch = { section: 'entries' }

export function parseLeagueGroupSection(value: unknown): LeagueGroupSection {
  return parseOneOf(LEAGUE_GROUP_SECTIONS, value) ?? DEFAULT_LEAGUE_GROUP_DETAIL_SEARCH.section
}

export const leagueGroupDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leagues/$id',
  params: numericIdParam,
  validateSearch: (search: Record<string, unknown>): LeagueGroupDetailSearch => ({
    section: parseLeagueGroupSection(search.section),
    matchday: parsePositiveInt(search.matchday) ?? undefined,
  }),
  search: { middlewares: [stripSearchParams(DEFAULT_LEAGUE_GROUP_DETAIL_SEARCH)] },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/league-group-detail.tsx')),
})

/** One section of the card as a link target. `validateSearch` makes `section` required on
 * every link here, so it is spelled once — `clubPlayersLink`'s reason. */
export function leagueGroupSectionLink(groupId: number, section: LeagueGroupSection) {
  return {
    to: leagueGroupDetailRoute.to,
    params: { id: groupId },
    search: { ...DEFAULT_LEAGUE_GROUP_DETAIL_SEARCH, section },
  } as const
}

/** The girone as a link target, so a table cell does not spell the path. */
export function leagueGroupCardLink(groupId: number) {
  return leagueGroupSectionLink(groupId, DEFAULT_LEAGUE_GROUP_DETAIL_SEARCH.section)
}
